import { CustomFieldProps, ReportTypeEnum } from '@nx-smartmonkey/shared/domain';
import { PendingReportCreate } from '../../application/ReportsQueueService';
import { ReportStatus, STOP_REPORT_STATUS } from '../../constants/Stops';
import { DriverProjectFields, DriversProjectFields } from '../../domain/driversProject/DriversProject';
import { StopReport } from '../../domain/report/StopReport';
import {
  CreateReportDataProps,
  ImageUploadedProps,
  ReportCreateLocation,
  StopReportCreate,
} from '../../domain/report/StopReportCreate';
import {
  StopReportCreateRepositoryDTO,
  StopReportCustomField,
} from '../../domain/report/StopReportCreateRepositoryDTO';
import { StopTaskMapper } from '../StopTaskMapper';

const convertCustomFields = (
  custom_fields: CustomFieldProps,
  serviceReportProjectField: DriverProjectFields
): StopReportCustomField => {
  const customFields: StopReportCustomField = {};

  for (const key in custom_fields) {
    const fieldInfo = serviceReportProjectField.find((field) => field.id === key && field.is_custom_field);

    if (fieldInfo) {
      switch (fieldInfo.type) {
        case `string`:
          customFields[key] = custom_fields[key].toString();
          break;
        case `boolean`:
          customFields[key] = Boolean(custom_fields[key]);
          break;
        case `number`:
          customFields[key] = Number(custom_fields[key]);
          break;
        case `select`:
        case `multiselect`:
          {
            const content = custom_fields[key] as number[];
            content.forEach((value) => {
              if (value === -1) {
                content.splice(content.indexOf(value), 1);
              }
            });
            if (content.length === 0) {
              delete customFields[key];
            } else {
              customFields[key] = content;
            }
          }
          break;
      }
    }
  }

  return customFields;
};

export class StopReportCreateMapper {
  static toDomain(props: {
    stopId: string;
    status: ReportStatus;
    projectFields: DriversProjectFields;
    currentPosition?: ReportCreateLocation;
    timeInStop?: number;
    images?: Array<ImageUploadedProps>;
    reportData?: CreateReportDataProps;
  }): StopReportCreate {
    const { stopId, status, projectFields, images, reportData, currentPosition, timeInStop } = props;
    return StopReportCreate.create(
      {
        stopId,
        status,
        images,
        reportData,
        location: currentPosition,
        timeInStop,
        createdAt: new Date(),
      },
      projectFields
    );
  }

  static toDummyReport(props: PendingReportCreate, report_id: string): StopReport {
    const { stopId, status, reportData, images } = props.data;
    const customFields = Object.keys(reportData?.customFields?.props ?? {}).length;

    return StopReport.create(
      {
        stop_id: stopId,
        images: images ? images.length > 0 : false,
        type:
          status === STOP_REPORT_STATUS.canceled
            ? ReportTypeEnum.SERVICE_REPORT_CANCELED
            : ReportTypeEnum.SERVICE_REPORT_COMPLETED,
        comments: reportData?.comments ? true : false,
        signature: reportData?.signature ? true : false,
        custom_fields: customFields > 0,
        tasks_completed:
          (reportData?.reportTasks ?? []).filter((task) => task.status === `completed`).length +
          (reportData?.temporalReportTasks ?? []).filter((task) => task.status === `completed`).length,
        tasks_total: (reportData?.reportTasks ?? []).length + (reportData?.temporalReportTasks ?? []).length,
        created_at: props.data.createdAt,
      },
      report_id
    );
  }

  static toRepository(report: StopReportCreate): StopReportCreateRepositoryDTO {
    const { status, stopId, projectFields, reportData, location, timeInStop } = report;

    const stopReportCreateRepositoryDTO: StopReportCreateRepositoryDTO = {
      stop_id: stopId,
      type:
        status === STOP_REPORT_STATUS.canceled
          ? ReportTypeEnum.SERVICE_REPORT_CANCELED
          : ReportTypeEnum.SERVICE_REPORT_COMPLETED,
      ...(report.images ? { images: report.images } : {}),
      ...(location ? { done_location: location } : {}),
      ...(timeInStop ? { time_in_stop: timeInStop } : {}),
      ...(reportData?.comments ? { comments: reportData.comments } : {}),
    };

    if (reportData) {
      if (status === STOP_REPORT_STATUS.canceled) {
        stopReportCreateRepositoryDTO.cancel_reason = reportData.cancelReason;
        stopReportCreateRepositoryDTO.custom_fields = reportData.customFields
          ? convertCustomFields(reportData.customFields!.props, projectFields.stop_canceled_fields)
          : undefined;
      } else {
        stopReportCreateRepositoryDTO.pin = reportData.pin;
        stopReportCreateRepositoryDTO.signature = reportData.signature;
        stopReportCreateRepositoryDTO.custom_fields = reportData.customFields
          ? convertCustomFields(reportData.customFields!.props, projectFields.stop_completed_fields)
          : undefined;

        if (reportData.reportTasks) {
          stopReportCreateRepositoryDTO.reportTasks = reportData.reportTasks.map((reportTask) => {
            const { task_id, status, comments, custom_fields } = reportTask.value;
            return {
              task_id,
              status,
              comments,
              custom_fields: custom_fields
                ? convertCustomFields(custom_fields!.props, projectFields.task_fields)
                : undefined,
            };
          });
        }

        if (reportData.temporalReportTasks) {
          stopReportCreateRepositoryDTO.temporalReportTasks = reportData.temporalReportTasks.map((reportTask) => {
            const { task_id, status, comments, custom_fields } = reportTask.value;

            return {
              task_id,
              status,
              comments,
              custom_fields:
                status !== `pending` && custom_fields
                  ? convertCustomFields(
                      custom_fields!.props,
                      status === `completed` ? projectFields.task_completed_fields : projectFields.task_canceled_fields
                    )
                  : undefined,
            };
          });
        }

        if (reportData.temporalTasks) {
          stopReportCreateRepositoryDTO.temporalTasks = reportData.temporalTasks?.map(StopTaskMapper.toRepository);
        }
      }
    }

    return stopReportCreateRepositoryDTO;
  }
}
